import type { ReferenceImageUrlHints } from './referenceImageUrl'
import { fetchRemoteImageBlob } from './remoteImageBlob'

async function toPngBlob(blob: Blob): Promise<Blob> {
  const bmp = await createImageBitmap(blob)
  try {
    const c = document.createElement('canvas')
    c.width = bmp.width
    c.height = bmp.height
    const ctx = c.getContext('2d')
    if (!ctx) throw new Error('无法创建画布')
    ctx.drawImage(bmp, 0, 0)
    const png = await new Promise<Blob>((resolve, reject) => {
      c.toBlob((b) => (b ? resolve(b) : reject(new Error('编码失败'))), 'image/png')
    })
    return png
  } finally {
    bmp.close()
  }
}

/**
 * 将图片 URL 对应像素复制到系统剪贴板（需 HTTPS / localhost 且浏览器支持 Clipboard API）。
 * `viewerUrl` 应与页面上正在展示的地址一致；部分环境仅接受 `image/png`，失败时会尝试转 PNG 再写。
 */
export async function copyImageToClipboard(
  viewerUrl: string,
  hints?: ReferenceImageUrlHints,
): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.write) {
    throw new Error('当前环境不支持复制图片')
  }
  if (typeof ClipboardItem === 'undefined') {
    throw new Error('当前浏览器不支持复制图片')
  }
  const trimmed = viewerUrl.trim()
  if (!trimmed) throw new Error('图片地址为空')

  const blob = await fetchRemoteImageBlob(trimmed, hints)
  const mime = blob.type && /^image\//i.test(blob.type) ? blob.type : 'image/png'

  const tryWrite = async (b: Blob, type: string) => {
    await navigator.clipboard.write([new ClipboardItem({ [type]: b })])
  }

  try {
    await tryWrite(blob, mime)
  } catch {
    const png = await toPngBlob(blob)
    await tryWrite(png, 'image/png')
  }
}
